import React, { Ref } from 'react';
import mergeRefs from 'react-merge-refs';

import { CheckboxGroup, CheckboxPanels } from '@eika/checkbox';
import { ErrorLabel } from '@eika/label';

import { CheckboxType, SvarId, Valideringstype } from '../../types/generated-types';
import { FocusableHtmlElement, useFocusRef } from '../hooks';
import { updateSvarId } from '../reducer/sporsmalsflyt-actions';
import { useSporsmalsflytDispatch } from '../reducer/sporsmalsflytDispatchProvider';
import { getFeilmeldingFrom } from '../reducer/valideringstype';
import { CurrentCheckboxAlternativ, CurrentCheckboxSporsmal } from './checkbox-types';

import './checkbox-feilmelding.scss';

interface CheckboxFeilmeldingProps {
    alternativer: CurrentCheckboxAlternativ[];
}

const CheckboxFeilmelding = ({ alternativer }: CheckboxFeilmeldingProps) => {
    const atLeastOneSelected =
        alternativer !== undefined && alternativer.filter((alternativ) => alternativ.checked).length > 0;
    if (!atLeastOneSelected) {
        return (
            <ErrorLabel text={getFeilmeldingFrom(Valideringstype.Pakrevd)} className="checkbox-feilmelding-wrapper" />
        );
    }
    return null;
};

export interface CheckboxOptions {
    label: string;
    checked: boolean;
    onValueChange: (checked: boolean, value?: string) => void;
    description?: string;
    value?: string;
    id?: string;
    ref?: Ref<HTMLInputElement>;
}

export const toOptions = (
    alternativer: CurrentCheckboxAlternativ[],
    onChange: (checked: boolean, value?: string) => void,
    optionRef: Ref<HTMLInputElement>,
    errorRef?: Ref<FocusableHtmlElement>,
    prefix = '',
): CheckboxOptions[] =>
    alternativer.map((alternativ, index) => ({
        label: alternativ.tekst,
        checked: alternativ.checked,
        onValueChange: onChange,
        value: alternativ.id,
        id: `${prefix}${alternativ.id}`,
        ref: index === 0 ? mergeRefs([optionRef, errorRef]) : undefined,
    }));

interface Props {
    currentSporsmal: CurrentCheckboxSporsmal;
    showFeilmelding: boolean;
    focusOnMount: boolean;
    errorRef?: Ref<FocusableHtmlElement>;
    prefix?: string;
}

export const Checkbox = ({ currentSporsmal, showFeilmelding, errorRef, prefix, focusOnMount = true }: Props) => {
    const optionRef = useFocusRef<HTMLInputElement>(focusOnMount);

    const dispatch = useSporsmalsflytDispatch();

    const onChange = (checked: boolean, value: SvarId) => {
        dispatch(updateSvarId(value));
    };

    const checkboxes = () => {
        if (currentSporsmal.sporsmal.checkboxType === CheckboxType.Buttons) {
            return (
                <CheckboxGroup
                    label={currentSporsmal.sporsmal.tekst}
                    sublabel={currentSporsmal.sporsmal.ingress}
                    options={toOptions(currentSporsmal.alternativer, onChange, optionRef, errorRef, prefix)}
                    key={`checkbox-sporsmal-${currentSporsmal.sporsmal.id}`}
                />
            );
        }

        return (
            <CheckboxPanels
                label={currentSporsmal.sporsmal.tekst}
                sublabel={currentSporsmal.sporsmal.ingress}
                options={toOptions(currentSporsmal.alternativer, onChange, optionRef, errorRef, prefix) as any[]}
                key={`checkbox-sporsmal-${currentSporsmal.sporsmal.id}`}
            />
        );
    };

    return (
        <div className="container__standard-width">
            {checkboxes()}
            {showFeilmelding && <CheckboxFeilmelding alternativer={currentSporsmal.alternativer} />}
        </div>
    );
};
